<template>
  <section id="contact" class="primary-bg">
    <v-container class="pb-16">

      <v-row justify="center">
        <h2 class="section-title">{{ lang.contact.caption }}</h2>
      </v-row>

      <v-row class="mt-4 d-flex align-center justify-center">
        <v-col cols="12" md="6" lg="6" el="4">
          <div v-if="submitted" class="text-white text-center">
            <i class="mdi mdi-check-circle-outline" style="font-size: 72px;color: green"></i>
            <h3>{{ lang.contact.formularSendCaption }}</h3>
            <p>{{ lang.contact.formularSendContent }}</p>
            <v-btn class="mt-4" @click="resetForm" color="white" variant="outlined">
              {{ lang.contact.sendAnotherMailButton }}
            </v-btn>
          </div>

          <v-form v-else v-model="formValid" @submit.prevent="submitForm">
            <v-text-field
                v-model="name"
                :label="lang.contact.placeholder_name"
                :rules="[]"
                required
            ></v-text-field>

            <v-text-field
                v-model="email"
                :label="lang.contact.placeholder_email"
                :rules="[rules.required, rules.email]"
                required
            ></v-text-field>

            <v-textarea
                v-model="message"
                :label="lang.contact.placeholder_nachricht"
                :rules="[rules.required]"
                required
                rows="3"
                auto-grow
            ></v-textarea>

            <v-btn type="submit" :class="formValid ? 'bg-green' : 'bg-black'" :disabled="!formValid">
              {{ lang.contact.send }}
            </v-btn>
          </v-form>

          <div v-if="mailError" class="text-white text-center">
            <i class="mdi mdi-alert-circle-outline" style="font-size: 72px;color: darkred"></i>
            <p v-html="lang.contact.sendMailError"></p>
          </div>
        </v-col>

        <v-col cols="12" md="6" lg="5" el="4" class="d-flex flex-column align-center justify-center text-center mb-16">
          <v-img eager :src="logoSrc" @error="logoErrorSrc" :height="300" :width="300"
                 class="flex-0-0"></v-img>
          <div class="d-flex">
            <v-icon icon="mdi-email"></v-icon>
            <a href="mailto:{{ lang.contact.email }}" class="ml-2" style="text-decoration: none; color: white">{{ lang.contact.email }}</a></div>

          <v-list class="bg-transparent">
            <v-list-item to="impressum">{{ lang.contact.impressum }}</v-list-item>
            <v-list-item to="datenschutz">{{ lang.contact.datenschutz }}</v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-container>

    <v-col class="pa-0">
      <svg class="waves" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
           viewBox="0 24 150 28" preserveAspectRatio="none" shape-rendering="auto">
        <defs>
          <path id="footer-wave"
                d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z"/>
        </defs>
        <g class="parallax">
          <use xlink:href="#footer-wave" x="48" y="0" fill="rgba(255,255,255,0.7"/>
          <use xlink:href="#footer-wave" x="48" y="3" fill="rgba(255,255,255,0.5)"/>
          <use xlink:href="#footer-wave" x="48" y="5" fill="rgba(255,255,255,0.3)"/>
          <use xlink:href="#footer-wave" x="48" y="7" fill="#fff"/>
        </g>
      </svg>
      <div class="copyright">
        <span class="mr-1 text-black">{{ copyright1 }}</span>
        <v-icon color="red">mdi-heart</v-icon>
        <span class="ml-1 text-black">{{ copyright2 }}</span>
      </div>
    </v-col>
  </section>
</template>

<style scoped>
section {
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.waves {
  position: relative;
  width: 100%;
  height: 100px;
  margin-bottom: -9px;
  min-height: 50px;
  max-height: 60px;
}

.copyright {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  height: 50px;
}

/* Animation */
.parallax > use {
  animation: move-forever 25s cubic-bezier(.55, .5, .45, .5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

p, div {
  color: white;
}

@media (max-width: 768px) {
  .waves {
    height: 40px;
    min-height: 40px;
  }
}
</style>

<script setup>
import {ref} from 'vue'

const name = ref('')
const email = ref('')
const message = ref('')
const formValid = ref(false)
const submitted = ref(false)
const mailError = ref(false)

const rules = {
  required: (v) => !!v || 'Pflichtfeld',
  email: (v) => /.+@.+\..+/.test(v) || 'Ungültige E-Mail-Adresse',
}

const submitForm = async () => {
  if (!formValid.value) return

  const formattedMessage = message.value.replace(/\n/g, '<br>')
  const payload = {
    name: name.value,
    email: email.value,
    message: formattedMessage,
  }

  try {
    const response = await fetch('https://api.dm-energieberatung.de/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      mailError.value = true
      const errorData = await response.json()
      console.error('Fehler vom Server:', errorData)
      return
    }

    submitted.value = true
    name.value = ''
    email.value = ''
    message.value = ''
  } catch (error) {
    console.error('Fetch Fehler:', error)
  }
}

const resetForm = () => {
  submitted.value = false
}
</script>

<script>
import lang from '@/lang/lang.js'

export default {
  data() {
    const currentYear = new Date().getFullYear();
    return {
      lang: lang,
      copyright1: `© ${currentYear} – Made with`,
      copyright2: 'in Germany',
      logoSrc: '/images/core/logo.webp',
      logoErrorSrc: '/images/core/logo.png',
    }
  }
}
</script>